import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { toEmbeddedRelativePath } from "@/surfaces/surface";
import { useEmbeddedHost } from "./embedded-host-provider";

/**
 * 嵌入端布局（设计 §5.5.1）。
 *
 * 首期采用**定高自滚**：宿主给定高度，内部自己滚。工单详情的消息流本来就需要
 * 独立滚动区和"滚到底部"行为，交给宿主页面滚会丢掉这些能力。
 * `frame:resize` 仍然上报，供宿主在小屏或抽屉式容器里做最小高度决策——宿主可以忽略，
 * 但不得把 iframe 高度设成小于 `minHeight`，否则内部滚动区会被压没。
 *
 * 这里刻意不复用 `WorkspaceLayout`：嵌入端不应出现双层侧栏、双层 Header
 * 和完整系统配置导航（§5.1 明确禁止靠 `isEmbedded` 裁剪独立端布局）。
 */

/** 低于这个高度内部滚动区就没有意义了，作为 `frame:resize` 的下限上报给宿主。 */
const MIN_FRAME_HEIGHT = 360;
/** 上报节流不低于 100ms，且高度变化小于 8px 时不发，避免与宿主 resize 互相触发形成循环。 */
const RESIZE_REPORT_INTERVAL_MS = 120;
const RESIZE_REPORT_THRESHOLD_PX = 8;

function useNavigationReport() {
  const { reportNavigation } = useEmbeddedHost();
  const location = useLocation();

  useEffect(() => {
    // 宿主可选把它写进自己的 URL 以保留深链；不实现也不影响嵌入端功能（§5.5.2）。
    reportNavigation(`${toEmbeddedRelativePath(location.pathname)}${location.search}`, document.title);
  }, [location.pathname, location.search, reportNavigation]);
}

function useContentHeightReport(contentRef: React.RefObject<HTMLElement | null>) {
  const { reportResize } = useEmbeddedHost();

  useEffect(() => {
    const node = contentRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return;
    }

    let lastReportedHeight = 0;
    let lastReportedAt = 0;
    let timer: number | undefined;

    const report = () => {
      timer = undefined;
      const contentHeight = Math.ceil(node.scrollHeight);
      if (Math.abs(contentHeight - lastReportedHeight) < RESIZE_REPORT_THRESHOLD_PX) {
        return;
      }
      lastReportedHeight = contentHeight;
      lastReportedAt = Date.now();
      reportResize(contentHeight, MIN_FRAME_HEIGHT);
    };

    const observer = new ResizeObserver(() => {
      if (timer !== undefined) {
        return;
      }
      const wait = Math.max(0, RESIZE_REPORT_INTERVAL_MS - (Date.now() - lastReportedAt));
      timer = window.setTimeout(report, wait);
    });

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [contentRef, reportResize]);
}

export function EmbeddedLayout() {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useNavigationReport();
  useContentHeightReport(contentRef);

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground" style={{ "--background": "var(--card)" } as React.CSSProperties}>
      {/* 定高自滚：阻止边界回弹和滚动串联，避免露出 iframe 文档或宿主的底色。 */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-none" ref={contentRef}>
        <Outlet />
      </div>
    </div>
  );
}
